import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgHcaptchaModule } from 'ng-hcaptcha';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

import { HTTP_INTERCEPTORS, HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  EventEmitter,
  Injectable,
  Input,
  NgModule,
  NO_ERRORS_SCHEMA,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import * as Sentry from '@sentry/browser';
import { MomentModule } from 'angular2-moment';
import { NouisliderModule } from 'ng2-nouislider';
import { TagInputModule } from 'ngx-chips';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { AppdashboardComponent } from './appdashboard/appdashboard.component';
import { AuthInterceptor } from './auth.interceptor';
import { AuthorizationComponent } from './authorization/authorization.component';
import { AuthorizationService } from './authorization/authorization.service';
import { UrlSignature } from './common/apiEndPoint';
import { LoginGuardService, PersonDetailsGuardService, AuthGuard } from './common/auth-guard-service';
import { CommonService } from './common/common.service';
import { NgbDateCustomParserFormatter } from './common/dateFormatter';
import { UtilService } from './common/util.service';
import { CongratulationService } from './congratulations/congratulation.service';
import { CongratulationsComponent } from './congratulations/congratulations.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ForgotPasswordService } from './forgot-password/forgot-password.service';
import { FrequentlyAskedQuestionsComponent } from './frequently-asked-questions/frequently-asked-questions.component';
import { GetstartedComponent } from './getstarted/getstarted.component';
import { GetstartedService } from './getstarted/getstarted.service';
import { HomeComponent } from './home/home.component';
import { InstructionsComponent } from './instructions/instructions.component';
import { LoginComponent } from './login/login.component';
import { LoginService } from './login/login.service';
import { OtppageComponent } from './otppage/otppage.component';
import { OtppageService } from './otppage/otppage.service';
import { PaymentfailureComponent } from './paymentfailure/paymentfailure.component';
import { PaymentsuccessComponent } from './paymentsuccess/paymentsuccess.component';
import { PortfolioReviewComponent } from './portfolio-review/portfolio-review.component';
import { PortfolioService } from './portfolio-review/portfolio-review.service';
import { PreviousUniversityComponent } from './previous-university/previous-university.component';
import { PreUniversityService } from './previous-university/previous.university.service';
import { ScholarshipComponent } from './scholarship/scholarship.component';
import { SerialPinloginComponent } from './serial-pinlogin/serial-pinlogin.component';
import { FooterComponent } from './shared/footer/footer.component';
import { ImageUploadModule } from './shared/image-upload/image-upload.module';
import { NavbarComponent } from './shared/navbar/navbar.component';
import { SponsorsParticularsComponent } from './sponsors-particulars/sponsors-particulars.component';
import { ResetpasswordemailComponent } from './resetpasswordemail/resetpasswordemail.component';
import { ResetnewpasswordComponent } from './resetnewpassword/resetnewpassword.component';
import { SessionService } from './session.service';
import { PaymentPendingComponent } from './payment-pending/payment-pending.component';
import { WassceeligibilitypopupComponent } from './wassceeligibilitypopup/wassceeligibilitypopup.component';


@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  extractError(error: any) {
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof Error) {
        return error.error;
      }
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }
      return error.message;
    }

    return null;
  }

  handleError(error: any) {
    const extractedError = this.extractError(error) || 'Handled unknown error';
    const eventId = Sentry.captureException(extractedError);

    if (!environment.production) {
      console.error(extractedError);
    }
    Sentry.showReportDialog({ eventId });
  }
}

// Minimal stub to satisfy template compilation for <re-captcha>.
@Component({
  selector: 'app-re-captcha',
  template: `<div class="recaptcha-stub" aria-hidden="true"></div>`,
})
export class ReCaptchaStubComponent {
  @Input() siteKey?: string;
  @Output() resolved = new EventEmitter<string>();
}

@NgModule({
  declarations: [
    AppComponent,
    ReCaptchaStubComponent,
    // REMOVED: BrowserModule, FormsModule, CommonModule, RouterModule from here
    CongratulationsComponent,
    ContactUsComponent,
    FooterComponent,
    ForgotPasswordComponent,
    FrequentlyAskedQuestionsComponent,
    HomeComponent,
    InstructionsComponent,
    LoginComponent,
    NavbarComponent,
    PortfolioReviewComponent,
    PreviousUniversityComponent,
    ScholarshipComponent,
    SponsorsParticularsComponent,
    GetstartedComponent,
    OtppageComponent,
    AuthorizationComponent,
    PaymentsuccessComponent,
    PaymentfailureComponent,
    SerialPinloginComponent,
    AppdashboardComponent,
    ResetpasswordemailComponent,
    ResetnewpasswordComponent,
    PaymentPendingComponent,
    WassceeligibilitypopupComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ImageUploadModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MomentModule,
    NgIdleKeepaliveModule.forRoot(),
    NgbModule,
    // REMOVED duplicate NgbModule
    NouisliderModule,
    RouterModule,
    TagInputModule,
    AppRoutingModule,
    NgMultiSelectDropDownModule.forRoot(),
    NgHcaptchaModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-top-center',
    }),
  ],
  exports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: NgbDateParserFormatter,
      useClass: NgbDateCustomParserFormatter,
    },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    CommonService,
    CongratulationService,
    ForgotPasswordService,
    LoginGuardService,
    LoginService,
    AuthGuard,
    PersonDetailsGuardService,
    PortfolioService,
    PreUniversityService,
    UrlSignature,
    UtilService,
    AuthorizationService,
    OtppageService,
    GetstartedService,
    SessionService,

    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

  ],
  bootstrap: [AppComponent],
})
export class AppModule {}